import React from "react";
import { Users, UserCog, Crown, Pencil, Ban, Building2, AlertTriangle, Briefcase } from "lucide-react";
import { deptName, managerLabel } from "../utils/names";

const DepartmentsTable = ({
  styles, t, language, can, departments, selectedIds, isMobile = false, showCompany = false,
  onToggleSelect, onViewMembers, onEdit, onManagers, onDeactivate,
}) => {
  const showActions = can.edit || can.assignManagers || can.delete;

  const renderSelect = (d, name) => (
    <input
      type="checkbox"
      className="dept-check"
      style={{ cursor: d.isActive ? "pointer" : "not-allowed" }}
      checked={selectedIds.has(d.id)}
      disabled={!d.isActive}
      onChange={() => onToggleSelect(d.id)}
      aria-label={name}
    />
  );

  // Only the current UI language is shown (no second-language line).
  const renderDept = (d, name) => (
    <div style={styles.deptCell}>
      <span style={styles.deptIcon(d.isActive)}>
        <Building2 size={19} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={styles.deptName}>{name}</div>
      </div>
    </div>
  );

  const renderMembers = (d) => (
    <button
      type="button"
      className="dept-chip-btn"
      style={{ ...styles.chip, cursor: "pointer" }}
      onClick={() => onViewMembers(d)}
      title={t.viewMembers}
    >
      <Users size={13} />
      {t.membersCount(d._count?.employees ?? 0)}
    </button>
  );

  const renderManagers = (managers) =>
    managers.length ? (
      <div style={styles.chipList}>
        {managers.map((m) => {
          const label = managerLabel(m, language);
          return (
            <span key={m.id} style={styles.chip} title={`${m.role === "MANAGER" ? t.manager : t.deputy}: ${label}`}>
              {m.role === "MANAGER" ? <Crown size={12} /> : <UserCog size={12} />}
              <span style={styles.chipText}>{label}</span>
            </span>
          );
        })}
      </div>
    ) : (
      <span style={styles.warnChip}>
        <AlertTriangle size={12} />
        {t.noManagers}
      </span>
    );

  const renderCompany = (d) => (
    <span style={styles.companyText}>
      <Briefcase size={13} style={{ flexShrink: 0 }} />
      {d.workingCompany}
    </span>
  );

  const renderStatus = (d) => (
    <span style={styles.statusChip(d.isActive)}>
      <span style={styles.statusDot(d.isActive)} />
      {d.isActive ? t.active : t.inactive}
    </span>
  );

  const renderActions = (d) => (
    <div style={styles.actionsCell}>
      {can.edit && (
        <button type="button" className="dept-action-btn" style={styles.actionBtn("neutral")}
          title={t.rename} aria-label={t.rename} onClick={() => onEdit(d)}>
          <Pencil size={14} />
        </button>
      )}
      {can.assignManagers && (
        <button type="button" className="dept-action-btn" style={styles.actionBtn("neutral")}
          title={t.managersTitle} aria-label={t.managersTitle} onClick={() => onManagers(d)}>
          <UserCog size={14} />
        </button>
      )}
      {can.delete && d.isActive && (
        <button type="button" className="dept-action-btn danger" style={styles.actionBtn("danger")}
          title={t.deactivate} aria-label={t.deactivate} onClick={() => onDeactivate(d)}>
          <Ban size={14} />
        </button>
      )}
    </div>
  );

  // ── phones: one card per department instead of a sideways-scrolling table ──
  if (isMobile) {
    return (
      <div style={styles.mobileList}>
        {departments.map((d) => {
          const name = deptName(d, language);
          return (
            <div key={d.id} className="dept-mobile-card" style={styles.mobileCard}>
              <div style={styles.mobileCardTop}>
                {can.merge && <div style={{ paddingTop: "11px" }}>{renderSelect(d, name)}</div>}
                <div style={{ flex: 1, minWidth: 0 }}>{renderDept(d, name)}</div>
                {renderStatus(d)}
              </div>
              <div style={styles.mobileCardMeta}>
                {showCompany && renderCompany(d)}
                {renderMembers(d)}
                {renderManagers(d.managers || [])}
              </div>
              {showActions && <div style={styles.mobileCardActions}>{renderActions(d)}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {can.merge && <th style={{ ...styles.th, width: "48px" }} aria-label={t.merge} />}
            <th style={{ ...styles.th, minWidth: "240px" }}>{t.name}</th>
            {showCompany && <th style={styles.th}>{t.company}</th>}
            <th style={styles.th}>{t.members}</th>
            <th style={styles.th}>{t.managers}</th>
            <th style={styles.th}>{t.status}</th>
            {showActions && <th style={{ ...styles.th, width: "1%" }}>{t.actions}</th>}
          </tr>
        </thead>
        <tbody>
          {departments.map((d) => {
            const name = deptName(d, language);
            return (
              <tr key={d.id} className="dept-row">
                {can.merge && <td style={styles.td}>{renderSelect(d, name)}</td>}
                <td style={styles.td}>{renderDept(d, name)}</td>
                {showCompany && <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{renderCompany(d)}</td>}
                <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{renderMembers(d)}</td>
                <td style={styles.td}>{renderManagers(d.managers || [])}</td>
                <td style={styles.td}>{renderStatus(d)}</td>
                {showActions && <td style={styles.td}>{renderActions(d)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DepartmentsTable;
